import React, { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Filter, Plus, Save, Trash2, Copy, Edit, ChevronDown } from "lucide-react";
import { Guest } from "@/data/mockData";
import { Checkbox } from "@/components/ui/checkbox";
import SearchBar from "@/components/SearchBar";
import { Link } from "react-router-dom";

// Mock data for companies and potential guests
const companies = [
  { id: "c1", name: "TechInnovate", industry: "Technology" },
  { id: "c2", name: "GreenEnergy", industry: "Renewable Energy" },
  { id: "c3", name: "MedSolutions", industry: "Healthcare" },
  { id: "c4", name: "FinTech Global", industry: "Finance" },
  { id: "c5", name: "EduLearn", industry: "Education" },
];

const potentialGuests = [
  { 
    id: "pg1", 
    name: "Alex Rivera", 
    title: "Chief Innovation Officer", 
    company: "TechInnovate", 
    expertise: ["AI", "Machine Learning", "Digital Transformation"],
    imageUrl: "https://randomuser.me/api/portraits/men/32.jpg"
  },
  { 
    id: "pg2", 
    name: "Taylor Kim", 
    title: "Head of Sustainability", 
    company: "GreenEnergy", 
    expertise: ["Renewable Energy", "Climate Tech", "Sustainable Business"],
    imageUrl: "https://randomuser.me/api/portraits/women/44.jpg"
  },
  { 
    id: "pg3", 
    name: "Dr. Jordan Patel", 
    title: "Medical Director", 
    company: "MedSolutions", 
    expertise: ["Healthcare Innovation", "Telemedicine", "Medical Research"],
    imageUrl: "https://randomuser.me/api/portraits/men/54.jpg"
  },
  { 
    id: "pg4", 
    name: "Morgan Chen", 
    title: "CTO", 
    company: "FinTech Global", 
    expertise: ["Blockchain", "Financial Technology", "Cybersecurity"],
    imageUrl: "https://randomuser.me/api/portraits/women/67.jpg"
  },
  { 
    id: "pg5", 
    name: "Sam Washington", 
    title: "Director of Learning", 
    company: "EduLearn", 
    expertise: ["Educational Technology", "Online Learning", "AI in Education"],
    imageUrl: "https://randomuser.me/api/portraits/men/76.jpg"
  },
];

// Mock saved prompts
const initialPrompts = [
  { 
    id: "p1", 
    title: "Tech Innovation", 
    content: "Can you share your thoughts on the most significant technological breakthroughs in your industry over the past year and how they might shape the future?" 
  },
  { 
    id: "p2", 
    title: "Leadership Journey", 
    content: "Could you walk us through your leadership journey and some of the key lessons you've learned along the way that have shaped your approach to management?" 
  },
  { 
    id: "p3", 
    title: "Industry Challenges", 
    content: "What do you see as the biggest challenges facing your industry today, and how are you and your organization addressing these challenges?" 
  },
];

const Explore = () => {
  const [searchPrompt, setSearchPrompt] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPromptDropdown, setShowPromptDropdown] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [selectedExpertise, setSelectedExpertise] = useState<string[]>([]);
  const [prompts, setPrompts] = useState(initialPrompts);
  const [currentPrompt, setCurrentPrompt] = useState({ id: "", title: "", content: "" });
  const [editMode, setEditMode] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  
  // Get all unique expertise areas
  const allExpertise = Array.from(
    new Set(potentialGuests.flatMap(guest => guest.expertise))
  );
  
  // Filter guests based on search term, company, and expertise
  const filteredGuests = potentialGuests.filter(guest => {
    const matchesSearch = searchTerm === "" || 
      guest.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      guest.expertise.some(e => e.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesCompany = selectedCompany === null || guest.company === selectedCompany;
    
    const matchesExpertise = selectedExpertise.length === 0 || 
      selectedExpertise.some(e => guest.expertise.includes(e));
    
    return matchesSearch && matchesCompany && matchesExpertise;
  });
  
  // Handle expertise toggle
  const toggleExpertise = (expertise: string) => {
    if (selectedExpertise.includes(expertise)) {
      setSelectedExpertise(selectedExpertise.filter(e => e !== expertise));
    } else {
      setSelectedExpertise([...selectedExpertise, expertise]);
    }
  };
  
  // Handle prompt operations
  const handleSavePrompt = () => {
    if (currentPrompt.title.trim() === "" || currentPrompt.content.trim() === "") {
      return;
    }
    
    if (editMode) {
      setPrompts(prompts.map(p => 
        p.id === currentPrompt.id ? currentPrompt : p
      ));
      setEditMode(false);
    } else {
      const newPrompt = {
        ...currentPrompt,
        id: `p${prompts.length + 1}`
      };
      setPrompts([...prompts, newPrompt]);
    }
    
    setCurrentPrompt({ id: "", title: "", content: "" });
  };
  
  const handleEditPrompt = (prompt: typeof prompts[0]) => {
    setCurrentPrompt(prompt);
    setEditMode(true);
  };
  
  const handleDeletePrompt = (id: string) => {
    setPrompts(prompts.filter(p => p.id !== id));
    if (currentPrompt.id === id) {
      setCurrentPrompt({ id: "", title: "", content: "" });
      setEditMode(false);
    }
  };

  const handleUsePrompt = (prompt: typeof prompts[0]) => {
    setSearchPrompt(prompt.content);
    setShowPromptDropdown(false);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchPrompt);
    setIsSearched(true);
  };
  
  return (
    <main className="flex flex-col min-h-screen bg-background">
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary">Explore Potential Guests</h1>
          <p className="text-muted-foreground mt-1">Discover and prepare for your next podcast guests</p>
        </div>
        
        <Tabs defaultValue="search">
          <TabsList className="mb-6 bg-card border border-border">
            <TabsTrigger 
              value="search" 
              className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
            >
              Find New Guests
            </TabsTrigger>
            <TabsTrigger 
              value="prompts"
              className="data-[state=active]:bg-accent data-[state=active]:text-accent-foreground"
            >
              Manage Prompts
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="search" className="space-y-6">
            <Card className="bg-card">
              <CardHeader className="px-4 pt-4">
                <CardTitle className="text-lg">Who are you looking for?</CardTitle>
              </CardHeader>
              <CardContent className="px-4 pt-0 pb-4 space-y-4">
                <div className="relative">
                  <textarea 
                    className="w-full min-h-[100px] px-3 py-2 rounded-md border border-input bg-card resize-none focus:outline-none focus:ring-1 focus:ring-accent"
                    placeholder="Enter your search or select a prompt..."
                    value={searchPrompt}
                    onChange={(e) => setSearchPrompt(e.target.value)}
                  />
                  <div className="mt-2 flex justify-between">
                    <div className="relative">
                      <Button 
                        variant="outline" 
                        className="border-accent/20"
                        onClick={() => setShowPromptDropdown(!showPromptDropdown)}
                      >
                        Use Saved Prompt <ChevronDown className="ml-2 h-4 w-4" />
                      </Button>
                      {showPromptDropdown && (
                        <div className="absolute z-10 mt-1 w-64 bg-card border border-border rounded-md shadow-lg">
                          <div className="max-h-60 overflow-y-auto p-1">
                            {prompts.map(prompt => (
                              <div 
                                key={prompt.id}
                                className="p-2 hover:bg-accent/10 rounded cursor-pointer"
                                onClick={() => handleUsePrompt(prompt)}
                              >
                                <div className="font-medium">{prompt.title}</div>
                                <div className="text-xs text-muted-foreground truncate">{prompt.content}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <Button 
                      className="bg-accent hover:bg-accent/90 text-accent-foreground"
                      onClick={handleSearchSubmit}
                    >
                      <Search className="mr-2 h-4 w-4" />
                      Search
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {isSearched && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-1 space-y-6">
                  <Card className="bg-card">
                    <CardHeader>
                      <CardTitle className="text-lg">Filter by Company</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div 
                        className={`px-3 py-2 rounded cursor-pointer transition ${selectedCompany === null ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/10'}`}
                        onClick={() => setSelectedCompany(null)}
                      >
                        All Companies
                      </div>
                      {companies.map(company => (
                        <div 
                          key={company.id}
                          className={`px-3 py-2 rounded cursor-pointer transition ${selectedCompany === company.name ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/10'}`}
                          onClick={() => setSelectedCompany(company.name)}
                        >
                          {company.name}
                          <div className="text-xs opacity-70">{company.industry}</div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                  
                  <Card className="bg-card">
                    <CardHeader>
                      <CardTitle className="text-lg">Filter by Expertise</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-wrap gap-2">
                        {allExpertise.map(expertise => (
                          <div 
                            key={expertise}
                            className={`px-3 py-1.5 text-sm rounded-full cursor-pointer transition ${selectedExpertise.includes(expertise) ? 'bg-accent text-accent-foreground' : 'bg-accent/10 hover:bg-accent/20'}`}
                            onClick={() => toggleExpertise(expertise)}
                          >
                            {expertise}
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                <div className="lg:col-span-2 space-y-4">
                  <h2 className="text-xl font-semibold text-primary">
                    Results {filteredGuests.length > 0 && `(${filteredGuests.length})`}
                  </h2>
                  
                  {filteredGuests.length === 0 ? (
                    <div className="text-center py-12 bg-card rounded-lg border">
                      <p className="text-muted-foreground">No guests match your search criteria.</p>
                      <Button variant="link" onClick={() => {
                        setSearchTerm("");
                        setSelectedCompany(null);
                        setSelectedExpertise([]);
                      }}>Clear all filters</Button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {filteredGuests.map(guest => (
                        <Card key={guest.id} className="bg-card">
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <img 
                                src={guest.imageUrl} 
                                alt={guest.name} 
                                className="w-14 h-14 rounded-full object-cover"
                              />
                              <div className="flex-1">
                                <h3 className="font-semibold text-lg">{guest.name}</h3>
                                <p className="text-muted-foreground">{guest.title} at {guest.company}</p>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {guest.expertise.map(exp => (
                                    <span key={exp} className="text-xs px-2 py-0.5 bg-accent/10 rounded-full">
                                      {exp}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary/10">
                                <Plus className="mr-1 h-3 w-3" />
                                Invite
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="prompts" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1 space-y-4">
                <Card className="bg-card">
                  <CardHeader>
                    <CardTitle className="text-lg">
                      {editMode ? "Edit Prompt" : "Create New Prompt"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="prompt-title">Prompt Title</label>
                      <Input 
                        id="prompt-title"
                        placeholder="E.g., Industry Trends"
                        value={currentPrompt.title}
                        onChange={(e) => setCurrentPrompt({...currentPrompt, title: e.target.value})}
                        className="bg-card"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium" htmlFor="prompt-content">Prompt Content</label>
                      <textarea 
                        id="prompt-content"
                        className="w-full min-h-[150px] px-3 py-2 rounded-md border border-input bg-card resize-none focus:outline-none focus:ring-1 focus:ring-accent"
                        placeholder="Write your interview prompt here..."
                        value={currentPrompt.content}
                        onChange={(e) => setCurrentPrompt({...currentPrompt, content: e.target.value})}
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="flex justify-between">
                    <Button 
                      variant="outline" 
                      onClick={() => {
                        setCurrentPrompt({ id: "", title: "", content: "" });
                        setEditMode(false);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleSavePrompt}
                      className="bg-accent hover:bg-accent/90 text-accent-foreground"
                    >
                      <Save className="mr-2 h-4 w-4" />
                      {editMode ? "Update Prompt" : "Save Prompt"}
                    </Button>
                  </CardFooter>
                </Card>
              </div>
              
              <div className="md:col-span-2">
                <h2 className="text-xl font-semibold mb-4 text-primary">Saved Prompts</h2>
                
                {prompts.length === 0 ? (
                  <div className="text-center p-8 bg-card rounded-lg border">
                    <p className="text-muted-foreground">No prompts saved yet.</p>
                    <p className="text-sm mt-2">Create your first prompt to get started.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {prompts.map(prompt => (
                      <Card key={prompt.id} className="bg-card">
                        <CardContent className="p-4">
                          <div className="flex justify-between items-start">
                            <h3 className="font-semibold">{prompt.title}</h3>
                            <div className="flex space-x-2">
                              <Button variant="ghost" size="sm" onClick={() => handleEditPrompt(prompt)}>
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => {
                                navigator.clipboard.writeText(prompt.content);
                                // In a real app, you would add a toast notification here
                              }}>
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeletePrompt(prompt.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="mt-2 text-sm">{prompt.content}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </main>
  );
};

export default Explore;
